import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { parseISO } from 'date-fns';

import { decodeTime } from '@dmb/core';

import { ButtonComponent, InlineSpinnerComponent, MenuService } from '../../components';
import { EventStatus, NotificationEvent, PushNotification } from '../../model/notification-settings';
import { NotificationCenterItemComponent } from '../item/notification-center-item.component';
import { NotificationService } from '../notification.service';

export type ListEntry = {
  id: string;
  status: EventStatus;
  title: string;
  message: string;
  downloadLink?: string;
  productLink?: string;
  validUntil?: Date;
  created: number; // Milliseconds since unix epoch
};

@Component({
  selector: 'dmb-notification-center-list',
  templateUrl: './notification-center-list.component.html',
  styleUrls: ['./notification-center-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, InlineSpinnerComponent, NotificationCenterItemComponent],
})
export class NotificationCenterListComponent {
  private allNotifications = signal<ListEntry[] | undefined>(undefined);
  onlyNew = signal(false);
  hasUnreadNotifications = output<boolean>();

  filteredNotifications = computed(() => {
    if (this.onlyNew()) {
      return this.allNotifications()?.filter((n) => n.status === EventStatus.CREATED);
    }

    return this.allNotifications();
  });

  constructor() {
    const notificationService = inject(NotificationService);
    const menuService = inject(MenuService);

    notificationService.messages.pipe(takeUntilDestroyed()).subscribe(this.onMessage.bind(this));
    notificationService.loadNotifications().then(this.onNotificationsLoaded.bind(this));

    effect(() => {
      const hasUnread = this.allNotifications()?.some((n) => n.status === EventStatus.CREATED) ?? false;
      this.hasUnreadNotifications.emit(hasUnread);
    });

    effect(async () => {
      const { previous, now } = menuService.lastUpdate();

      if (previous !== 'notifications' || now === 'notifications') {
        return;
      }

      // Has to be untracked, so only menu changes trigger the effect
      const unreadIds = untracked(() => this.allNotifications())
        ?.filter((n) => n.status === EventStatus.CREATED)
        .map((n) => n.id);

      if (unreadIds === undefined || unreadIds.length === 0) {
        return;
      }

      const updatedIds = await notificationService.markAsRead(unreadIds);

      this.allNotifications.update((old) =>
        old?.map((n) => (updatedIds.includes(n.id) ? { ...n, status: EventStatus.READ } : n)),
      );
    });
  }

  private onMessage(message: PushNotification) {
    const newMessage: ListEntry = {
      id: message.data.eventId,
      status: EventStatus.CREATED,
      title: message.notification.title,
      message: message.notification.body,
      downloadLink: message.data.downloadLink,
      productLink: message.data.productLink,
      created: decodeTime(message.data.eventId),
    };

    this.allNotifications.update((old) => {
      if (!old) {
        return [newMessage];
      }

      if (old.some((o) => o.id === newMessage.id)) {
        return old;
      }

      return [newMessage, ...old];
    });
  }

  private onNotificationsLoaded(notifications: NotificationEvent[]) {
    const loadedEntries = notifications.map<ListEntry>((event) => ({
      id: event.eventId,
      status: event.eventStatus,
      title: event.title,
      message: event.message,
      downloadLink: event.payload?.downloadLink,
      productLink: event.payload?.productLink,
      validUntil: event.validUntil === undefined ? undefined : parseISO(event.validUntil),
      created: decodeTime(event.eventId),
    }));

    this.allNotifications.update((old) => {
      if (!old) {
        return loadedEntries;
      }

      const unknownOlds = old.filter((o) => loadedEntries.every((r) => r.id !== o.id));

      // Unknown entries are assumed to have just come in/be the newest
      return [...unknownOlds, ...loadedEntries];
    });
  }
}
